import type { TaxRule, TaxProfile, TaxCategory, TaxBreakdown, TaxComputation } from '@/core/types/tax.types';

/* ─── Default Tax Rules (India GST) ─────────────────────────────── */

export const DEFAULT_TAX_RULES: TaxRule[] = [
  // National: CGST (Central GST)
  { id: 'cgst-room-budget', name: 'CGST Room (Budget)', displayName: 'CGST', level: 'national', category: 'room', method: 'percentage', rate: 6, isCompound: false, enabled: true },
  { id: 'cgst-room-luxury', name: 'CGST Room (Luxury)', displayName: 'CGST', level: 'national', category: 'room', method: 'percentage', rate: 9, isCompound: false, enabled: true },
  { id: 'cgst-fnb', name: 'CGST F&B', displayName: 'CGST', level: 'national', category: 'fnb', method: 'percentage', rate: 2.5, isCompound: false, enabled: true },
  { id: 'cgst-service', name: 'CGST Services', displayName: 'CGST', level: 'national', category: 'service', method: 'percentage', rate: 9, isCompound: false, enabled: true },
  { id: 'cgst-banquet', name: 'CGST Banquet', displayName: 'CGST', level: 'national', category: 'banquet', method: 'percentage', rate: 9, isCompound: false, enabled: true },
  { id: 'cgst-spa', name: 'CGST Spa', displayName: 'CGST', level: 'national', category: 'spa', method: 'percentage', rate: 9, isCompound: false, enabled: true },

  // State: SGST (State GST)
  { id: 'sgst-room-budget', name: 'SGST Room (Budget)', displayName: 'SGST', level: 'state', category: 'room', method: 'percentage', rate: 6, isCompound: false, enabled: true },
  { id: 'sgst-room-luxury', name: 'SGST Room (Luxury)', displayName: 'SGST', level: 'state', category: 'room', method: 'percentage', rate: 9, isCompound: false, enabled: true },
  { id: 'sgst-fnb', name: 'SGST F&B', displayName: 'SGST', level: 'state', category: 'fnb', method: 'percentage', rate: 2.5, isCompound: false, enabled: true },
  { id: 'sgst-service', name: 'SGST Services', displayName: 'SGST', level: 'state', category: 'service', method: 'percentage', rate: 9, isCompound: false, enabled: true },
  { id: 'sgst-banquet', name: 'SGST Banquet', displayName: 'SGST', level: 'state', category: 'banquet', method: 'percentage', rate: 9, isCompound: false, enabled: true },
  { id: 'sgst-spa', name: 'SGST Spa', displayName: 'SGST', level: 'state', category: 'spa', method: 'percentage', rate: 9, isCompound: false, enabled: true },

  // Local: Municipal / City taxes
  { id: 'municipal-all', name: 'Municipal Tax', displayName: 'Municipal', level: 'local', category: 'all', method: 'percentage', rate: 0, isCompound: false, enabled: false },
  { id: 'tourism-room', name: 'Tourism Levy', displayName: 'Tourism Levy', level: 'local', category: 'room', method: 'flat', rate: 0, isCompound: false, enabled: false },
];

/* ─── Default GST Config ─────────────────────────────────────────── */

export const DEFAULT_GST_CONFIG = {
  cgstRate: 9,
  sgstRate: 9,
  igstRate: 18,
  gstNumber: '',
  filingFrequency: 'monthly' as const,
  lastFiled: '2026-02-28',
};

/* ─── Default Profile ────────────────────────────────────────────── */

export const DEFAULT_TAX_PROFILE: TaxProfile = {
  country: 'India',
  state: 'Maharashtra',
  city: 'Mumbai',
  taxRegime: 'GST',
  gstConfig: DEFAULT_GST_CONFIG,
  rules: DEFAULT_TAX_RULES,
};

/* ─── Category Labels ────────────────────────────────────────────── */

export const TAX_CATEGORY_LABELS: Record<TaxCategory, string> = {
  room: 'Room Charges',
  fnb: 'Food & Beverage',
  service: 'Services',
  banquet: 'Banquet & Events',
  spa: 'Spa & Wellness',
  laundry: 'Laundry',
  transport: 'Transport',
  all: 'All Categories',
};

export const TAX_CATEGORY_OPTIONS = Object.entries(TAX_CATEGORY_LABELS).map(([value, label]) => ({ value, label }));

export const TAX_LEVEL_LABELS = {
  national: 'National / Central',
  state: 'State / Regional',
  local: 'City / Local',
} as const;

export const TAX_REGIME_OPTIONS = [
  { label: 'GST (India)', value: 'GST' },
  { label: 'VAT (Europe)', value: 'VAT' },
  { label: 'Sales Tax (US)', value: 'Sales Tax' },
  { label: 'Custom', value: 'Custom' },
];

export const GST_FILING_OPTIONS = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
];

/* ─── Tax Calculation Utility ────────────────────────────────────── */

export function calculateTax(
  amount: number,
  category: TaxCategory,
  rules: TaxRule[],
): TaxComputation {
  const applicable = rules.filter(r => r.enabled && (r.category === category || r.category === 'all'));
  let runningBase = amount;
  const breakdown: TaxBreakdown[] = [];

  for (const rule of applicable) {
    const taxableAmount = rule.isCompound ? runningBase : amount;
    const taxAmount = rule.method === 'percentage'
      ? Math.round(taxableAmount * rule.rate) / 100
      : rule.rate;

    breakdown.push({
      ruleId: rule.id,
      ruleName: rule.displayName,
      rate: rule.rate,
      taxableAmount,
      taxAmount,
    });

    if (rule.isCompound) runningBase += taxAmount;
  }

  const totalTax = breakdown.reduce((s, b) => s + b.taxAmount, 0);
  return { subtotal: amount, breakdown, totalTax, grandTotal: amount + totalTax };
}

/* ─── Convenience GST Calculator ───────────────────────────────── */

/**
 * Simple GST calculation for booking/folio flows.
 * India GST slabs: rooms < 7500/night = 12% (6+6), >= 7500 = 18% (9+9)
 * F&B = 5% (2.5+2.5), Services/Banquet/Spa = 18% (9+9)
 */
export function computeGST(
  amount: number,
  category: TaxCategory = 'room',
  roomTariffPerNight?: number,
): { cgst: number; sgst: number; total: number; effectiveRate: number } {
  let cgstPct = 9;
  let sgstPct = 9;

  if (category === 'room') {
    const isLuxury = (roomTariffPerNight ?? 7500) >= 7500;
    cgstPct = isLuxury ? 9 : 6;
    sgstPct = isLuxury ? 9 : 6;
  } else if (category === 'fnb') {
    cgstPct = 2.5;
    sgstPct = 2.5;
  }

  const cgst = Math.round(amount * cgstPct) / 100;
  const sgst = Math.round(amount * sgstPct) / 100;
  return { cgst, sgst, total: cgst + sgst, effectiveRate: (cgstPct + sgstPct) / 100 };
}
